/**
 * EventRuleService
 * Events hub Stage 1B link rules: conditions on an event's signals (tags, type, campus, venue and so
 * on) that suggest links or set the coordinator. Rules never change type, campus or venue.
 * Route: events/api/events/rules (Admin role OR Event.Rule.Manage)
 */
import { BaseService } from '../base/BaseService';
import { unwrapEventsResponse as unwrap } from './unwrapEventsResponse';
export class EventRuleService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? EventRuleService.DEFAULT_SERVICE_PATH,
            basePath: EventRuleService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /** GET api/events/rules. */
    async list() {
        const res = await this.listRaw();
        return res.data ?? [];
    }
    listRaw() {
        return this.http.get(this.buildUrl());
    }
    /** GET api/events/rules/{ruleId}. */
    async get(ruleId) {
        return unwrap(await this.getRaw(ruleId), `Rule ${ruleId} not found`);
    }
    getRaw(ruleId) {
        return this.http.get(this.buildUrl(String(ruleId)));
    }
    /** POST api/events/rules. */
    async create(request) {
        return unwrap(await this.createRaw(request), 'Could not create the rule');
    }
    createRaw(request) {
        return this.http.post(this.buildUrl(), request);
    }
    /** PUT api/events/rules/{ruleId}. Turning AutoApply on records who did it. */
    async update(ruleId, request) {
        return unwrap(await this.updateRaw(ruleId, request), `Could not update rule ${ruleId}`);
    }
    updateRaw(ruleId, request) {
        return this.http.put(this.buildUrl(String(ruleId)), request);
    }
    /** DELETE api/events/rules/{ruleId}. Suggestions it made and approved links stay. */
    async delete(ruleId) {
        const res = await this.deleteRaw(ruleId);
        if (res.success === false)
            throw new Error(res.message || `Could not delete rule ${ruleId}`);
        return true;
    }
    deleteRaw(ruleId) {
        return this.http.delete(this.buildUrl(String(ruleId)));
    }
    /** POST api/events/rules/preview. Dry run of an unsaved rule over upcoming events. */
    async previewUnsaved(request) {
        return unwrap(await this.previewUnsavedRaw(request), 'Could not preview the rule');
    }
    previewUnsavedRaw(request) {
        return this.http.post(this.buildUrl('preview'), request);
    }
    /** POST api/events/rules/{ruleId}/preview. Dry run of a saved rule (required before turning on AutoApply). */
    async preview(ruleId) {
        return unwrap(await this.previewRaw(ruleId), `Could not preview rule ${ruleId}`);
    }
    previewRaw(ruleId) {
        return this.http.post(this.buildUrl(`${ruleId}/preview`));
    }
    /** POST api/events/rules/re-evaluate. Queues every upcoming event for the rules (202). */
    async reEvaluate() {
        const res = await this.reEvaluateRaw();
        if (res.success === false)
            throw new Error(res.message || 'Could not re-evaluate the upcoming events');
        return res.data ?? { queued: true };
    }
    reEvaluateRaw() {
        return this.http.post(this.buildUrl('re-evaluate'));
    }
}
EventRuleService.DEFAULT_SERVICE_PATH = 'events';
EventRuleService.BASE_PATH = 'api/events/rules';
//# sourceMappingURL=EventRuleService.js.map